import { config } from '@/config';
import { IParamsApi } from '@/types/apis/params';
import { IResponseApi } from '@/types/apis/response';
import { IPreview } from '@/types/components/preview';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ISetupCheckoutVerify } from '../slice/checkoutVerification.slice';
import { TermsType } from './../../constants/enum';

const params: {
  shop?: string;
} = {};
if (config.shop) {
  params.shop = config.shop;
}
export const apiCaller = createApi({
  reducerPath: 'apiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_END_POINT,
    prepareHeaders: async (headers) => {
      headers.set('authorization', config.instance ? config.instance : config.urlParams ? config.urlParams : '');
      headers.set('ngrok-skip-browser-warning', 'ngrok');

      return headers;
    },
  }),
  tagTypes: [
    'general',
    'preview',
    'overview',
    'language',
    'translation',
    'checkout_verify',
    'popup',
    'rule_name',
    'preview_label',
    'month_label',
    'term_and_condition',
    'total_rule_active',
    'plan',
    'language_used',
  ],
  // get data settings
  endpoints: (builder) => ({
    getGeneralDetail: builder.query<IResponseApi.GeneralDetail, void>({
      query: () => {
        return {
          url: `shop/general-setting`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'general' }],
    }),
    updateAppStatus: builder.mutation<IResponseApi.CommonResponse, boolean>({
      query: (input) => {
        return {
          url: `settings/status-app`,
          method: 'PUT',
          params: { ...params },
          body: {
            appStatus: input,
            shop: params.shop,
          },
        };
      },
      invalidatesTags: [{ type: 'general' }],
    }),
    updateAppSettings: builder.mutation<IResponseApi.CommonResponse, IParamsApi.IShopSetting | undefined>({
      query: (input) => {
        return {
          url: `shop/settings`,
          method: 'PUT',
          params: { ...params },
          body: {
            ...input,
            shop: params.shop,
          },
        };
      },
      invalidatesTags: [{ type: 'general' }, { type: 'translation' }],
    }),

    getOverview: builder.query<IResponseApi.IOverview, IParamsApi.IOverview>({
      query: (input) => {
        return {
          url: `analytics`,
          method: 'GET',
          params: { ...params, startDate: input.startDate, endDate: input.endDate },
        };
      },
      providesTags: [{ type: 'overview' }],
    }),

    getShopDomain: builder.query<IResponseApi.IDomain, undefined>({
      query: () => {
        return {
          url: `shop/domain`,
          method: 'GET',
          params: { ...params },
        };
      },
    }),

    uploadImage: builder.mutation<IResponseApi.IUploadImage, { image: FormData; oldFileName?: string }>({
      query: (input) => {
        return {
          url: `file/upload-image`,
          method: 'POST',
          body: input.image,
          params: { ...params, shop: config.shop, oldFileName: input.oldFileName },
        };
      },
    }),

    getRuleName: builder.query<IResponseApi.IRuleName, void>({
      query: () => {
        return {
          url: `settings/rule-name`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'rule_name' }],
    }),

    updateTemplate: builder.mutation<IResponseApi.CommonResponse, IPreview>({
      query: (input) => {
        return {
          url: `settings`,
          method: 'PUT',
          params: { ...params },
          body: input,
        };
      },
      invalidatesTags: [{ type: 'popup' }, { type: 'rule_name' }, { type: 'total_rule_active' }],
    }),

    getSettings: builder.query<IResponseApi.IGetSettings, string>({
      query: (input) => {
        return {
          url: 'settings',
          method: 'GET',
          params: { ...params, id: input },
        };
      },
      providesTags: [{ type: 'preview' }],
    }),

    getPopupList: builder.query<IResponseApi.IPopupList, IParamsApi.IPopupList>({
      query: (input) => {
        return {
          url: 'settings/all',
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: [{ type: 'popup' }],
    }),

    deleteRule: builder.mutation<IResponseApi.CommonResponse, string>({
      query: (input) => {
        return {
          url: `settings`,
          method: 'DELETE',
          params: { ...params, ids: input },
        };
      },
      invalidatesTags: () => {
        return [{ type: 'popup' }, { type: 'rule_name' }, { type: 'translation' }, { type: 'total_rule_active' }];
      },
    }),

    deleteAllRule: builder.mutation<IResponseApi.CommonResponse, void>({
      query: () => {
        return {
          url: `settings/all`,
          method: 'DELETE',
          params: { ...params },
        };
      },
      invalidatesTags: [{ type: 'popup' }, { type: 'rule_name' }, { type: 'translation' }, { type: 'total_rule_active' }],
    }),

    updateSetupCheckoutVerify: builder.mutation<IResponseApi.CommonResponse, ISetupCheckoutVerify>({
      query: (input) => {
        return {
          url: `settings/checkout`,
          method: 'PUT',
          params: { ...params },
          body: input,
        };
      },
      invalidatesTags: [{ type: 'checkout_verify' }, { type: 'total_rule_active' }],
    }),

    getSetupCheckoutVerify: builder.query<IResponseApi.IGetSetupCheckoutVerify, void>({
      query: () => {
        return {
          url: 'settings/checkout',
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'checkout_verify' }],
    }),

    updateTermAndCondition: builder.mutation<
      IResponseApi.CommonResponse,
      IParamsApi.ISetupTermAndCondition | IParamsApi.ISetupTermAndConditionCheckoutUI
    >({
      query: (input) => {
        return {
          url: `terms/upsert`,
          method: 'POST',
          params: { ...params },
          body: input,
        };
      },
      invalidatesTags: [{ type: 'term_and_condition' }, { type: 'total_rule_active' }],
    }),

    getTermAndCondition: builder.query<IResponseApi.ITermAndCondition, { type: TermsType; shop: string }>({
      query: (input) => {
        return {
          url: 'terms/details',
          method: 'GET',
          params: { ...params, type: input.type, shop: input.shop },
        };
      },
      providesTags: [{ type: 'term_and_condition' }],
    }),

    getPages: builder.query<IResponseApi.IGetPages, void>({
      query: () => {
        return {
          url: 'shopify/pages',
          method: 'GET',
          params: { ...params },
        };
      },
    }),
    trackAction: builder.mutation<void, { shop: string; event: string }>({
      query: (input) => {
        return {
          url: 'tracking',
          method: 'POST',
          params: { ...params },
          body: {
            shop: input.shop,
            event: input.event,
          },
        };
      },
    }),

    getProducts: builder.query<IResponseApi.IGetProductList, { filter: string; page: number; perPage: number }>({
      query: (input) => {
        return {
          url: `wix/get-shop-products`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),

    getCollections: builder.query<IResponseApi.IGetCollectionList, { filter: string; page: number; perPage: number }>({
      query: (input) => {
        return {
          url: `wix/get-shop-collections`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),

    getTranslation: builder.query<IResponseApi.ITranslations, { page?: number; limit?: number }>({
      query: (input) => {
        return {
          url: `translation/all`,
          method: 'GET',
          params: { ...input, ...params },
        };
      },
      providesTags: [{ type: 'translation' }],
    }),
    getSingleTranslation: builder.query<IResponseApi.ISingleTranslationRes, { id: undefined | string }>({
      query: (input) => {
        return {
          url: `translation`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: (result, error, { id }) => [{ type: 'translation', id }],
    }),
    updateTranslation: builder.mutation<IResponseApi.ITranslations, IParamsApi.IUpdateTranslation>({
      query: (input) => {
        return {
          url: `translation`,
          method: 'POST',
          params: { ...params },
          body: {
            shop: params.shop,
            ...input,
          },
        };
      },
      invalidatesTags: (result, error, input) => [
        { type: 'translation', id: input.id },
        { type: 'translation' },
        { type: 'total_rule_active' },
        { type: 'language_used' },
      ],
    }),
    deleteTranslation: builder.mutation<IResponseApi.CommonResponse, { ids: string[] }>({
      query: (input) => {
        return {
          url: `translation`,
          method: 'DELETE',
          params: { ...params, ids: JSON.stringify(input.ids) },
        };
      },
      invalidatesTags: [{ type: 'translation' }, { type: 'total_rule_active' }, { type: 'language_used' }],
    }),
    getPreviewLabel: builder.query<IResponseApi.IPreviewLabel, { id: string }>({
      query: (input) => {
        return {
          url: `settings/preview-label`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: (result, error, input) => [{ type: 'preview_label', id: input.id }],
    }),
    getMonthLabel: builder.query<IResponseApi.IMonthLabel, undefined>({
      query: () => {
        return {
          url: `settings/month-label`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'month_label' }],
    }),
    fetchGA: builder.mutation<IResponseApi.CommonResponse, IParamsApi.IGA4>({
      query: (input) => {
        return {
          url: `tracking/ga4`,
          method: 'POST',
          params: { ...params },
          body: { ...input },
        };
      },
    }),

    // Start Plan Pricing
    getPlans: builder.query<IResponseApi.IListPricing, void>({
      query: () => {
        return {
          url: `app-pricing`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'plan' }],
    }),

    getLinkChargePlan: builder.mutation<IResponseApi.IGetLinkChargePlan, IParamsApi.ILinkAccountPlan>({
      query: (input) => {
        return {
          url: 'shop/charge',
          method: 'GET',
          params: {
            ...params,
            subscription: input.subscription,
            plan: input.plan,
          },
        };
      },
      invalidatesTags: [{ type: 'general' }, { type: 'total_rule_active' }, { type: 'popup' }, { type: 'translation' }],
    }),

    getTotalRuleActive: builder.query<IResponseApi.IGetTotalRuleActive, void>({
      query: () => {
        return {
          url: `shop/total-rule`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'total_rule_active' }],
    }),

    applyDiscountCode: builder.mutation<IResponseApi.CommonResponse, string>({
      query: (discountCode) => {
        return {
          url: 'promotion/apply-discount-code',
          method: 'PUT',
          params: { ...params },
          body: { shop: params.shop, code: discountCode },
        };
      },
      invalidatesTags: [{ type: 'plan' }],
    }),

    getAllLanguageUsed: builder.query<IResponseApi.IGetAllLanguageUsed, void>({
      query: () => {
        return {
          url: `translation/language-used`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: [{ type: 'language_used' }],
    }),
  }),
});
